import type { CommunicationMessage, CommunicationThread } from "./contracts";

/**
 * Regras de leitura da tela de Mensagens.
 *
 * Elas ficam fora do componente porque respondem perguntas de domínio — o que
 * é uma conversa aberta, quantas estão sem resposta, qual mensagem resume a
 * conversa — e precisam de teste próprio, sem montar a interface inteira.
 */

export const CHAT_VIEWS = ["active", "unread", "archived"] as const;
export type ChatView = (typeof CHAT_VIEWS)[number];

export const CHAT_VIEW_LABEL: Record<ChatView, string> = {
  active: "Abertas",
  unread: "Não lidas",
  archived: "Arquivadas",
};

export function isChatView(value: string | null): value is ChatView {
  return CHAT_VIEWS.includes((value ?? "") as ChatView);
}

/** Última mensagem da conversa; é ela que aparece na lista. */
export function lastMessageOf(
  messages: readonly CommunicationMessage[],
  threadId: string,
): CommunicationMessage | undefined {
  let latest: CommunicationMessage | undefined;
  for (const message of messages) {
    if (message.threadId !== threadId || message.state === "draft") continue;
    if (!latest || message.occurredAt > latest.occurredAt) latest = message;
  }
  return latest;
}

/** Rascunho guardado da conversa, que o protótipo mostra como "Rascunho". */
export function draftOf(
  messages: readonly CommunicationMessage[],
  threadId: string,
): CommunicationMessage | undefined {
  return messages.find((message) => message.threadId === threadId && message.state === "draft");
}

export function threadMessages(
  messages: readonly CommunicationMessage[],
  threadId: string,
): CommunicationMessage[] {
  return messages
    .filter((message) => message.threadId === threadId && message.state !== "draft")
    .sort((left, right) => left.occurredAt.localeCompare(right.occurredAt));
}

export interface ChatFilter {
  /** `all` cobre os canais de chat; a lixeira é recorte, não canal. */
  channel: "all" | "whatsapp" | "instagram" | "messenger" | "trash";
  view: ChatView;
  query: string;
}

function folds(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

/**
 * A lixeira é excludente: uma conversa descartada não aparece em nenhuma outra
 * lista, mesmo que continue aberta ou não lida.
 */
export function filterChatThreads(
  threads: readonly CommunicationThread[],
  messages: readonly CommunicationMessage[],
  filter: ChatFilter,
): CommunicationThread[] {
  const query = folds(filter.query.trim());
  return threads
    .filter((thread) => thread.channel !== "email")
    .filter((thread) => {
      if (filter.channel === "trash") return Boolean(thread.trashedAt);
      if (thread.trashedAt) return false;
      if (filter.channel !== "all" && thread.channel !== filter.channel) return false;
      if (filter.view === "unread") return thread.unread;
      if (filter.view === "archived") return thread.state === "resolved";
      return thread.state !== "resolved";
    })
    .filter((thread) => {
      if (!query) return true;
      const last = lastMessageOf(messages, thread.id);
      return folds(`${thread.subject} ${thread.counterpartAddress} ${last?.bodyPreview ?? ""}`).includes(query);
    })
    .sort((left, right) => right.lastMessageAt.localeCompare(left.lastMessageAt));
}

/**
 * Quantas mensagens recebidas esperam leitura.
 *
 * O contrato guarda `unread` como sim/não, e a lista do protótipo mostra um
 * número. Ele é derivado, não um segundo campo a manter: são as mensagens
 * recebidas depois da última que saiu daqui.
 */
export function unreadCountOf(
  messages: readonly CommunicationMessage[],
  thread: CommunicationThread,
): number {
  if (!thread.unread) return 0;
  const since = thread.lastOutboundAt ?? "";
  return messages.filter(
    (message) =>
      message.threadId === thread.id &&
      message.direction === "inbound" &&
      message.occurredAt > since,
  ).length;
}

/** Contagem por canal usada na navegação lateral, sempre sem a lixeira. */
export function chatChannelCounts(threads: readonly CommunicationThread[]): {
  all: number;
  whatsapp: number;
  instagram: number;
  messenger: number;
  trash: number;
} {
  const counts = { all: 0, whatsapp: 0, instagram: 0, messenger: 0, trash: 0 };
  for (const thread of threads) {
    if (thread.channel === "email") continue;
    if (thread.trashedAt) {
      counts.trash += 1;
      continue;
    }
    if (thread.state === "resolved") continue;
    counts.all += 1;
    counts[thread.channel] += 1;
  }
  return counts;
}

/** Conversas não lidas: o número do subtítulo da página. */
export function unreadChatCount(threads: readonly CommunicationThread[]): number {
  return threads.filter((thread) => thread.channel !== "email" && !thread.trashedAt && thread.unread).length;
}

export interface MessageDay {
  /** Data civil `AAAA-MM-DD` em `America/Sao_Paulo`. */
  day: string;
  label: string;
  messages: CommunicationMessage[];
}

const TIME_ZONE = "America/Sao_Paulo";

export function civilDay(iso: string, now = new Date()): string {
  const formatter = new Intl.DateTimeFormat("en-CA", { timeZone: TIME_ZONE });
  void now;
  return formatter.format(new Date(iso));
}

export function dayLabel(day: string, now = new Date()): string {
  const today = civilDay(now.toISOString());
  if (day === today) return "hoje";
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (day === civilDay(yesterday.toISOString())) return "ontem";
  const [year, month, date] = day.split("-").map(Number);
  return new Intl.DateTimeFormat("pt-BR", {
    day: "numeric",
    month: "short",
    year: year === now.getFullYear() ? undefined : "numeric",
    timeZone: TIME_ZONE,
  }).format(new Date(Date.UTC(year, month - 1, date, 12)));
}

/** Agrupa a conversa por dia, como as tarjas de data do protótipo. */
export function groupMessagesByDay(
  messages: readonly CommunicationMessage[],
  now = new Date(),
): MessageDay[] {
  const groups: MessageDay[] = [];
  for (const message of messages) {
    const day = civilDay(message.occurredAt);
    const last = groups.at(-1);
    if (last && last.day === day) last.messages.push(message);
    else groups.push({ day, label: dayLabel(day, now), messages: [message] });
  }
  return groups;
}

/** Hora curta da lista e do rodapé de cada mensagem. */
export function shortTime(iso: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: TIME_ZONE,
  }).format(new Date(iso));
}

/**
 * Carimbo da lista: hora quando é de hoje, dia da semana na semana corrente e
 * data curta antes disso — a mesma escada do protótipo.
 */
export function listStamp(iso: string, now = new Date()): string {
  const day = civilDay(iso);
  const today = civilDay(now.toISOString());
  if (day === today) return shortTime(iso);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (day === civilDay(yesterday.toISOString())) return "ontem";
  const elapsed = now.getTime() - new Date(iso).getTime();
  if (elapsed < 7 * 24 * 60 * 60 * 1000)
    return new Intl.DateTimeFormat("pt-BR", { weekday: "short", timeZone: TIME_ZONE })
      .format(new Date(iso))
      .replace(".", "");
  return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", timeZone: TIME_ZONE }).format(
    new Date(iso),
  );
}

/* Inbox de e-mail --------------------------------------------------------- */

export const MAIL_FOLDERS = [
  "inbox",
  "unread",
  "sent",
  "drafts",
  "starred",
  "archived",
  "trash",
] as const;
export type MailFolder = (typeof MAIL_FOLDERS)[number];

export const MAIL_FOLDER_LABEL: Record<MailFolder, string> = {
  inbox: "Entrada",
  unread: "Não lidos",
  sent: "Enviados",
  drafts: "Rascunhos",
  starred: "Favoritos",
  archived: "Arquivados",
  trash: "Lixeira",
};

export function isMailFolder(value: string | null): value is MailFolder {
  return MAIL_FOLDERS.includes((value ?? "") as MailFolder);
}

/**
 * A pasta é um recorte da conversa, não uma cópia dela.
 *
 * Lixeira e arquivados são excludentes — uma conversa descartada não aparece na
 * Entrada, mesmo continuando não lida. As demais pastas leem o que a conversa
 * tem: mensagem recebida, mensagem enviada, rascunho guardado ou favorito.
 */
export function mailFolderOf(
  thread: CommunicationThread,
  messages: readonly CommunicationMessage[],
): MailFolder[] {
  if (thread.trashedAt) return ["trash"];
  const own = messages.filter((message) => message.threadId === thread.id);
  const folders: MailFolder[] = [];
  if (thread.starredAt) folders.push("starred");
  if (own.some((message) => message.state === "draft")) folders.push("drafts");
  if (own.some((message) => message.direction === "outbound" && message.state !== "draft")) folders.push("sent");
  if (thread.state === "resolved") {
    folders.push("archived");
    return folders;
  }
  if (own.some((message) => message.direction === "inbound")) folders.push("inbox");
  if (thread.unread) folders.push("unread");
  return folders;
}

export function filterMailThreads(
  threads: readonly CommunicationThread[],
  messages: readonly CommunicationMessage[],
  folder: MailFolder,
  query = "",
): CommunicationThread[] {
  const term = folds(query.trim());
  return threads
    .filter((thread) => thread.channel === "email")
    .filter((thread) => mailFolderOf(thread, messages).includes(folder))
    .filter((thread) => {
      if (!term) return true;
      const last = lastMessageOf(messages, thread.id);
      return folds(
        `${thread.subject} ${thread.counterpartAddress} ${last?.bodyPreview ?? ""}`,
      ).includes(term);
    })
    .sort((left, right) => right.lastMessageAt.localeCompare(left.lastMessageAt));
}

export function mailFolderCounts(
  threads: readonly CommunicationThread[],
  messages: readonly CommunicationMessage[],
): Record<MailFolder, number> {
  const counts = { inbox: 0, unread: 0, sent: 0, drafts: 0, starred: 0, archived: 0, trash: 0 };
  for (const thread of threads) {
    if (thread.channel !== "email") continue;
    for (const folder of mailFolderOf(thread, messages)) counts[folder] += 1;
  }
  return counts;
}

/**
 * Carimbo da lista de e-mail: hora no mesmo dia, data curta no mesmo ano e data
 * com ano antes disso — a escada do protótipo.
 */
export function mailStamp(iso: string, now = new Date()): string {
  const day = civilDay(iso);
  if (day === civilDay(now.toISOString())) return shortTime(iso);
  const date = new Date(iso);
  return new Intl.DateTimeFormat("pt-BR", {
    day: "numeric",
    month: "short",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
    timeZone: TIME_ZONE,
  }).format(date);
}

/** Data longa do cabeçalho de cada carta. */
export function mailFullStamp(iso: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: TIME_ZONE,
  }).format(new Date(iso));
}
